import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Box, Button, InputBase, Portal, Switch, Typography } from '@mui/material';
import {
  MaestroDebugContext,
  MaestroSchemaContext,
  MaestroPreviewView,
  MaestroJsonViewerView,
  MaestroContextViewerView,
  MaestroTelemetryView,
} from '@ae/maestrov2-form-lib';
import { clampDockPosition, dockAnchorSx } from './dockPosition';
import {
  getDrafts,
  subscribeDrafts,
  setDraftFragment,
  clearDrafts,
  resolveProfile,
  deviationCount,
} from './copilotProfileDraft';
import {
  DEFAULT_COPILOT_PROFILE,
  copilotProfile,
} from '../../../config/copilotChromeProfiles';
import DragBar from './DragBar';
import HostConfigView from './HostConfigView';
import { canvasViewport, canvasRect } from './copilotCanvas';
import { usePointerDrag } from './usePointerDrag';

/*
 * What the settings cog used to switch, minus anything this window cannot
 * show. Each view below renders nothing until its own flag is on, so they are
 * all safe to mount and the switch is the only thing that reveals them.
 */
const SWITCHES = [
  // `view` marks the ones that put something in the dock. Latching and
  // fragment borders change how the rest behaves; they draw nothing of it.
  { key: 'showPreview', label: 'Preview', view: true },
  { key: 'previewLatched', label: 'Latch to window' },
  { key: 'showJsonViewer', label: 'Payload', view: true },
  { key: 'showContextViewer', label: 'Binding context', view: true },
  { key: 'showTelemetry', label: 'Telemetry', view: true },
  { key: 'showFragmentBorders', label: 'Fragment borders' },
  { key: 'showHostConfig', label: 'Host config', view: true },
  // Reveals an inputs fragment the profile has switched off, so a graphic
  // set to hide inputs can be checked against what it is hiding. Nothing
  // appears when the caption authors none, which is itself the answer.
  { key: 'showHiddenInputs', label: 'Hidden inputs' },
];

const ORIGIN_TITLE = {
  default: 'the default — nothing recorded for this graphic',
  committed: 'already recorded in the host config',
  changed: 'decided here — not written down yet',
};

const PREVIEW_URL_ID = 'copilot-preview-renderer-url';

/*
 * The fragments a graphic can switch off, in the order they appear down the
 * window, so the row reads like the thing it is editing.
 */
const FRAGMENTS = [
  { key: 'showNavigator', label: 'Nav' },
  { key: 'showInputs', label: 'Inputs' },
  { key: 'showSummary', label: 'Summary' },
  { key: 'showActions', label: 'Actions' },
  { key: 'showHeader', label: 'Header' },
  { key: 'showForm', label: 'Form' },
];

const COPY_SX = {
  minWidth: 0,
  px: 1,
  py: 0.125,
  fontSize: '0.72rem',
  color: '#E2861B',
  border: '1px solid rgba(226,134,27,0.45)',
  '&:hover': { borderColor: '#E2861B', bgcolor: 'rgba(226,134,27,0.08)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.12)' },
};

const SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#E2861B' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#E2861B' },
};

/*
 * Where a fragment's current value came from. Walking the grid to work out
 * which definitions need writing, the useful question is not on-or-off but
 * "is this already recorded somewhere, or did I just decide it?" - so the
 * colour answers that and the position answers on/off.
 *
 * Compared by VALUE, not by presence in the draft: touching a graphic seeds
 * its draft from the committed entry, so a key can sit in the draft while
 * saying exactly what the committed map already said.
 */
const ORIGIN = {
  default:   { tint: 'rgba(255,255,255,0.55)', label: 'rgba(255,255,255,0.7)' },
  committed: { tint: '#4FC3F7', label: '#4FC3F7' },   // already written down
  changed:   { tint: '#E2861B', label: '#E2861B' },   // decided here, not yet written
};

const originOf = (live, committed, key) => {
  if (live[key] !== committed[key]) return 'changed';
  return live[key] === DEFAULT_COPILOT_PROFILE[key] ? 'default' : 'committed';
};

const switchSx = (origin) => ({
  '& .MuiSwitch-switchBase.Mui-checked': { color: ORIGIN[origin].tint },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: ORIGIN[origin].tint,
  },
});

/**
 * The floating dock the developer switches fill.
 *
 * Composed beside the Dialog rather than inside it, so what it holds is not
 * torn down when the window closes. Every view in it renders nothing until its
 * own uiSettings flag is on, so it is safe to mount unconditionally, and a
 * preview left open survives hiding the strip.
 *
 * `previewLatched` decides whether the dock goes with the window. Latched is
 * how drive mode behaves; unlatched keeps the renderer warm across a close and
 * reopen, which is the point of having it out here.
 *
 * Portalled to the body because react-grid-layout gives every grid item a
 * transform, and a transformed ancestor makes `position: fixed` resolve
 * against that ancestor instead of the viewport — the dock would be drawn a
 * grid column away from where it was dropped. The Dialog beside it escapes
 * the same trap by being portalled too.
 *
 * The switches that fill it live at the top of the caption window; this holds
 * only what they reveal, so the preview can outlive the window that switched
 * it on. The preview is not itself a developer tool — only the switch that
 * turns it on is — so the dock is gated on the views' own flags and not on
 * the developer key.
 *
 * @param {boolean} windowOpen whether the caption window is showing
 */
export function DeveloperDock({ windowOpen = true }) {
  const debug = useContext(MaestroDebugContext);
  const setUiSettings = debug?.setUiSettings;
  const saved = debug?.uiSettings?.devDockPosition || null;

  const dockRef = useRef(null);
  /*
   * A callback ref, not just a ref object: the dock renders nothing until a
   * view is switched on, so an effect keyed on anything else runs while
   * there is no element to measure — and an unmeasured dock is allowed to
   * sit anywhere, which is how it ended up below the bottom of the screen.
   */
  const [dockEl, setDockEl] = useState(null);
  const setDock = useCallback((node) => { dockRef.current = node; setDockEl(node); }, []);
  const [position, setPosition] = useState(saved);
  const latest = useRef(saved);

  /*
   * A smaller window, or a position remembered from a larger screen, must not
   * strand the dock outside it — so the same clamp runs once on mount and
   * again on every resize.
   */
  /*
   * Nothing switched on means no panel, so an empty bar never floats there.
   * Held as a value rather than an early return because the clamp below has
   * to re-run when the dock appears: until it does there is no element to
   * measure, and an unmeasured dock is allowed to sit anywhere.
   */
  const anyViewOn = SWITCHES.some(({ key, view }) => view && debug?.uiSettings?.[key]);
  const visible = anyViewOn && !(debug?.uiSettings?.previewLatched && !windowOpen);

  useEffect(() => {
    if (!dockEl) return undefined;

    const pullIntoView = () => setPosition((current) => clampDockPosition(
      current,
      canvasRect(dockRef.current?.getBoundingClientRect()),
      canvasViewport(),
    ));

    pullIntoView();
    window.addEventListener('resize', pullIntoView);

    /*
     * The dock also changes size when a view is switched on, which the window
     * resize event says nothing about — switching on a tall panel pushed its
     * own buttons below the bottom of the screen with no way to reach them.
     */
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(pullIntoView)
      : null;
    if (observer) observer.observe(dockEl);

    return () => {
      window.removeEventListener('resize', pullIntoView);
      observer?.disconnect();
    };
  }, [dockEl]);

  /* The press records where the dock was; each move offsets from there. */
  const origin = useRef(null);

  const { dragging, startDrag } = usePointerDrag({
    onStart: () => {
      const box = canvasRect(dockRef.current?.getBoundingClientRect());
      origin.current = box ? { left: box.left, top: box.top } : null;
    },
    onMove: (delta) => {
      if (!origin.current) return;
      const next = clampDockPosition(
        { left: origin.current.left + delta.x, top: origin.current.top + delta.y },
        canvasRect(dockRef.current?.getBoundingClientRect()),
        canvasViewport(),
      );
      latest.current = next;
      setPosition(next);
    },
    // Persisted once on release, the way the preview persists its width.
    onEnd: () => setUiSettings?.((previous) => ({ ...previous, devDockPosition: latest.current })),
  });

  if (!visible) return null;

  return (
    <Portal>
      <Box
        ref={setDock}
        sx={{
          position: 'fixed',
          ...dockAnchorSx(position),
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          // No gap: the bar is the window's own titlebar, not a control
          // floating above it.
          gap: 0,
          maxHeight: '80vh',
          width: 'fit-content',
        }}
      >
        <DragBar label="Move developer views" onPointerDown={startDrag} dragging={dragging} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, overflowY: 'auto' }}>
          <MaestroPreviewView />
          <MaestroJsonViewerView />
          <MaestroContextViewerView />
          <MaestroTelemetryView />
          {debug?.uiSettings?.showHostConfig && <HostConfigView />}
        </Box>
      </Box>
    </Portal>
  );
}

/**
 * The developer strip: what the settings cog used to switch, at the top of the
 * caption window the cog used to sit on.
 *
 * Reads the provider's debug slice directly, so the switches write the same
 * uiSettings the cog wrote. Nothing here is reachable without the key
 * combination in useDeveloperPanel.
 */
export default function DeveloperPanel({ graphicId }) {
  const debug = useContext(MaestroDebugContext);
  const schema = useContext(MaestroSchemaContext);
  const uiSettings = debug?.uiSettings;
  const setUiSettings = debug?.setUiSettings;

  const toggle = (key) => () =>
    setUiSettings?.((previous) => ({ ...previous, [key]: !previous?.[key] }));

  /*
   * The renderer the preview loads. uiSettings wins over whatever the host
   * passed in, and an empty value falls back to it, so clearing the field is
   * how you get the host's renderer back rather than a broken preview.
   *
   * Held as a draft and written on Enter or blur: writing per keystroke would
   * reload the iframe on every character typed.
   */
  const saved = uiSettings?.previewRendererUrl ?? '';
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);

  const commitUrl = () =>
    setUiSettings?.((previous) => ({ ...previous, previewRendererUrl: draft.trim() }));

  /*
   * The fragment editor. Flipping a switch writes a deviation for THIS graphic
   * only; setting it back to the default removes it again, so what the Host
   * config panel shows is just the exceptions. Reading the whole map, copying
   * it and pasting one back live in that panel, which has room for a block of
   * source; this row is for the graphic in front of you.
   */
  const drafts = useSyncExternalStore(subscribeDrafts, getDrafts);
  // The same merge the window uses. Computing it a second time here is how
  // the switches and the window could disagree about one graphic.
  const live = resolveProfile(drafts, graphicId);
  // What the config file alone would give — the baseline the colour compares to.
  const committed = copilotProfile(graphicId);

  const onUrlKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); return; }
    if (e.key === 'Escape') { e.stopPropagation(); setDraft(saved); e.currentTarget.blur(); }
  };

  return (
    <Box
      role="group"
      aria-label="Developer controls"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // One rhythm down the strip; each row owns its own internal spacing.
        gap: 1.25,
        px: 2,
        py: 1.5,
        borderRadius: '4px',
        /*
         * Opaque rather than a tinted wash: the switch labels sit on the
         * window's own dark panel and lost their contrast against it.
         */
        bgcolor: '#141414',
        border: '1px solid rgba(226,134,27,0.45)',
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          columnGap: 2.25, rowGap: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: '#E2861B',
          }}
        >
          Developer
        </Typography>
        {SWITCHES.map(({ key, label }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Switch
              size="small"
              checked={Boolean(uiSettings?.[key])}
              onChange={toggle(key)}
              slotProps={{ input: { 'aria-label': label } }}
              sx={SWITCH_SX}
            />
            <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', ml: 'auto' }}>
          Ctrl+Alt+D to hide
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography
          component="label"
          htmlFor={PREVIEW_URL_ID}
          sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}
        >
          Preview renderer
        </Typography>
        <InputBase
          id={PREVIEW_URL_ID}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitUrl}
          onKeyDown={onUrlKeyDown}
          placeholder={schema?.previewRendererUrl || 'No renderer set'}
          spellCheck={false}
          sx={{
            flex: 1, minWidth: 0,
            px: 1, py: 0.25,
            fontSize: '0.8rem',
            color: '#fff',
            bgcolor: '#0d0d0d',
            border: '1px solid rgba(255,255,255,0.16)',
            borderRadius: '3px',
            '&.Mui-focused': { borderColor: '#E2861B' },
          }}
        />
        {schema?.previewRendererId && (
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
            {schema.previewRendererId}
          </Typography>
        )}
      </Box>

      {/*
        Which fragments THIS graphic shows. Settled by looking at the window,
        so it belongs next to the window rather than in a file that needs a
        rebuild. Copy out when the set is complete.
      */}
      <Box
        role="group"
        aria-label="Window fragments for this graphic"
        sx={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          columnGap: 2.25, rowGap: 0.75,
          pt: 1.25,
          borderTop: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        <Typography
          sx={{
            fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)',
            whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis',
            // Reads as the row's heading, so it sits a little apart from the
            // switches it labels.
            fontWeight: 600, letterSpacing: '0.02em', mr: 0.5,
          }}
          title={graphicId || ''}
        >
          {graphicId || 'No graphic'}
        </Typography>

        {FRAGMENTS.map(({ key, label }) => {
          const origin = originOf(live, committed, key);
          return (
            <Box
              key={key}
              data-origin={origin}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <Switch
                size="small"
                checked={Boolean(live[key])}
                disabled={!graphicId}
                onChange={(e) => setDraftFragment(graphicId, key, e.target.checked)}
                slotProps={{ input: { 'aria-label': label } }}
                sx={switchSx(origin)}
              />
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                  color: ORIGIN[origin].label,
                }}
                title={ORIGIN_TITLE[origin]}
              >
                {label}
              </Typography>
            </Box>
          );
        })}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
            {deviationCount(drafts)} changed
          </Typography>
          <Button
            size="small"
            onClick={clearDrafts}
            disabled={!Object.keys(drafts).length}
            sx={COPY_SX}
          >
            Reset
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
